'use client';

import { Button, Card, InlineToast, Input } from '@clickt/brand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { ThemedLogo } from '../components/ThemedLogo.tsx';
import { authClient } from '../lib/auth-client.ts';

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: async (vars: { email: string; password: string }) => {
      const { data, error } = await authClient.signIn.email({
        email: vars.email,
        password: vars.password,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setErrorMessage(null);
      qc.removeQueries({ queryKey: ['me'] });
      void navigate({ to: '/app' });
    },
    onError: (err: { message?: string; code?: string }) => {
      setErrorMessage(
        err.code === 'EMAIL_NOT_VERIFIED'
          ? 'Verify your email first. Check your inbox for the link.'
          : (err.message ?? 'Sign in failed.'),
      );
    },
  });

  // Stay in the loading state from click through navigation: on success we're
  // leaving the page, so the button must NOT snap back to "Sign in" — otherwise
  // the gap before the /app route's spinner reads as a failed attempt.
  const isSubmitting = loginMutation.isPending || loginMutation.isSuccess;

  return (
    <div className="min-h-screen flex flex-col surface-dotted">
      <header className="flex items-center px-6 lg:px-10 py-6">
        <a
          href="https://clickt.link"
          className="flex items-center rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
          aria-label="clickt home"
        >
          <ThemedLogo className="h-8 w-auto" />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md space-y-6">
          <div className="space-y-3 text-center">
            <p className="text-caption uppercase tracking-[0.08em] font-medium text-electric">
              Welcome back
            </p>
            <h1 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
              Sign in to clickt.
            </h1>
            <p className="text-body-md text-cool-gray">
              See which videos drove revenue while you slept.
            </p>
          </div>

          <Card padding="lg">
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                loginMutation.mutate({ email, password });
              }}
            >
              {errorMessage ? (
                <InlineToast tone="danger" title="Couldn't sign in." description={errorMessage} />
              ) : null}
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                placeholder="you@yourdomain.com"
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={1}
                autoComplete="current-password"
                placeholder="••••••••••"
              />
              <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in…' : 'Sign in'}
                {!isSubmitting && <ArrowRight className="h-4 w-4" />}
              </Button>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-[14px] text-cool-gray hover:text-midnight transition-colors duration-fast rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  Forgot password?
                </Link>
              </div>
            </form>
          </Card>

          <p className="text-center text-[14px] text-cool-gray">
            No account?{' '}
            <Link
              to="/signup"
              className="text-midnight font-medium hover:text-electric transition-colors duration-fast rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              Sign up
            </Link>
          </p>

          <p className="text-center text-[12px] text-cool-gray">
            <a
              href="https://clickt.link/terms"
              className="hover:text-midnight transition-colors duration-fast"
            >
              Terms
            </a>
            {' · '}
            <a
              href="https://clickt.link/privacy"
              className="hover:text-midnight transition-colors duration-fast"
            >
              Privacy
            </a>
            {' · '}
            <a
              href="https://clickt.link/refund"
              className="hover:text-midnight transition-colors duration-fast"
            >
              Refund policy
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
